import { Header } from "@/components/SiteUI";
import { useEffect, useState, useCallback } from "react";
import questions from "./data/beck";
import { Button } from "@/components/ui/button";
import tests from "@/shared/api/tests";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { useAuth } from "@/hooks/useAuth";
import { useAnonResults } from "@/hooks/useAnonResults";

export const BeckTest = () => {
  const [scores, setScores] = useState([]); // массив Map-ов
  const [question, setQuestion] = useState(0);
  const [error, setError] = useState(null);
  const [checkedAnswers, setCheckedAnswers] = useState(new Map());
  const [results, setResults] = useState();

  const navigate = useNavigate();
  const { isAuth, loading } = useAuth();
  const { saveResult } = useAnonResults();

  const handleAnswerClick = (id, score) => {
    const newChecked = new Map(checkedAnswers);
    if (newChecked.has(id)) {
      newChecked.delete(id);
    } else {
      newChecked.set(id, score);
    }
    setCheckedAnswers(newChecked);
  };

  const handleNextClick = useCallback(async () => {
    if (!checkedAnswers.size) {
      setError("Вы не ответили на вопрос! Выберите хотя бы один ответ.");
      return;
    }

    setError(null);

    if (question + 1 < questions.length) {
      setScores((prev) => {
        const updated = [...prev];
        updated[question] = checkedAnswers;
        return updated;
      });

      const nextAnswers = scores[question + 1] || new Map();
      setCheckedAnswers(nextAnswers);
      setQuestion((q) => q + 1);
    } else {
      const totalScores = [...scores, checkedAnswers]

      try {
        const res = await tests.saveBeckTest(totalScores.map(map => Object.fromEntries(map)));
        console.log(res.data)
        if (res.data?._id) {
          toast.success("Тест сохранен");
          if (!isAuth && !loading) {
            saveResult(res.data._id);
          }
          navigate(`/tests/results/${res.data?._id}`)
        }
      } catch (err) {
        console.error(err);
        toast.error("Ошибка сохранения теста");
      }
    }
  }, [checkedAnswers, question, questions.length, scores]);

  const handleBackClick = () => {
    if (question > 0) {
      const prevAnswers = scores[question - 1] || new Map();
      setCheckedAnswers(prevAnswers);
      setQuestion((q) => q - 1);
    }
  };

  // 👉 Обработка клавиши Enter
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === "Enter" && !e.shiftKey) {
        e.preventDefault();
        handleNextClick();
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [handleNextClick]);

  const current = questions[question];

  return (
    <div className="min-h-screen px-4 sm:px-8 py-8">
      <Header />
      <div className="bg-card rounded-lg border p-6 mt-16">
        <h2 className="my-4 text-center">Шкала депрессии Бека</h2>
        <div className="text-muted-foreground mb-4">
          <p>
            Внимательно прочитайте каждую группу утверждений. Затем определите в
            каждой группе одно утверждение, которое лучше всего соответствует
            тому, как вы себя чувствовали НА ЭТОЙ НЕДЕЛЕ И СЕГОДНЯ.
          </p>
          <p>
            Поставьте галочку около выбранного утверждения. Если несколько
            утверждений из одной группы кажутся вам одинаково хорошо
            подходящими, то поставьте галочки около каждого из них. Прежде чем
            сделать свой выбор, убедитесь, что вы прочли все утверждения в
            каждой группе.
          </p>
        </div>

        {results === undefined && current ? (
          <div className="flex flex-col border rounded-lg items-center justify-start">
            <h3 className="text-center mb-4">{current.question}</h3>

            <div className="flex flex-col gap-2 max-w-lg w-full">
              {current.answers.map((item, i) => (
                <div key={item.id} className="p-4 border rounded-lg">
                  <input
                    type="checkbox"
                    id={`answer-${i}`}
                    onChange={() => handleAnswerClick(item.id, item.score)}
                    checked={checkedAnswers.has(item.id)}
                    className="checkbox mr-2 w-4 h-4 cursor-pointer"
                  />
                  <label htmlFor={`answer-${i}`} className="cursor-pointer">
                    {item.answer}
                  </label>
                </div>
              ))}

              {error && <div className="text-red-500 text-center">{error}</div>}

              <div className="flex justify-between gap-2 max-w-lg w-full mt-4">
                <Button
                  variant="outline"
                  onClick={handleBackClick}
                  disabled={question === 0}
                >
                  Назад
                </Button>
                <Button onClick={handleNextClick}>
                  {question + 1 === questions.length ? "Завершить" : "Далее"}
                </Button>
              </div>
            </div>

            <div className="mt-4 flex flex-col items-center gap-2 w-full">
              <div className="h-1 w-full bg-neutral-200 dark:bg-neutral-600">
                <div
                  className="h-1 bg-primary transition-all"
                  style={{
                    width: `${(100 / questions.length) * (question + 1)}%`,
                  }}
                />
              </div>
              <span className="text-muted-foreground">
                {question + 1} из {questions.length}
              </span>
            </div>
          </div>
        ) : (
          <div className="w-64 h-64 flex flex-col gap-4 pt-12 items-center justify-center">
            <Loader2 className="w-12 h-12 animate-spin" />
            <span className="text-muted-foreground">Обработка результатов...</span>
          </div>
        )}


      </div>
    </div>
  );
};
